import axios from 'axios';

// Interface definitions
export interface IntegrationComponent {
    name: string;
    title: string;
    description: string;
    thumb?: string;
    label?: string;
}

export interface Integration {
    id: string;
    name: string;
    description?: string;
    componentName: string;
    contentBasic?: string;
    contentAdvanced?: string;
    contentYaml?: string;
    triggerName?: string;
    containerId?: string;
    runningStatus?: string;
    orgId?: string[];
    createdDate?: string;
    createdBy?: string;
    lastModifiedDate?: string;
    lastModifiedBy?: string;
}

export interface PaginatedResponse<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
}

// Get auth token from localStorage
const getAuthToken = (): string => {
    try {
        const tokenData = localStorage.getItem('oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app');
        if (tokenData) {
            const parsedData = JSON.parse(tokenData);
            return parsedData.access_token || '';
        }
        return '';
    } catch (error) {
        console.error('Error retrieving access token:', error);
        return '';
    }
};

// Create API instances
const designerApi = axios.create({
    baseURL: import.meta.env.MODE === 'development' ? '/designer/api' : '/designer/api',
});

const dockerApi = axios.create({
    baseURL: import.meta.env.MODE === 'development' ? '/designer/api/docker' : '/designer/api/docker',
});

// Add request interceptor for auth token
designerApi.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

dockerApi.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Get all available integration components
export const getAllComponents = async (): Promise<IntegrationComponent[]> => {
    const response = await designerApi.get<IntegrationComponent[]>('/integrations/components/all');
    return response.data;
};

// Get component schema for configuration
export const getComponentSchema = async (componentName: string): Promise<any> => {
    const response = await designerApi.get(`/integrations/component-schema/${componentName}`);
    return response.data;
};

// Get list of integrations for a component
export const getComponentList = async (
    componentName: string,
    page: number = 0,
    size: number = 10
): Promise<PaginatedResponse<Integration>> => {
    const response = await designerApi.get<PaginatedResponse<Integration>>(
        `/integrations/components/${componentName}`,
        { params: { page, size, sort: 'createdDate,DESC' } }
    );
    return response.data;
};

// Create a new integration
export const createIntegration = async (integration: Partial<Integration>): Promise<Integration> => {
    const response = await designerApi.post<Integration>('/integrations', integration);
    return response.data;
};

// Update/patch an integration
export const patchIntegration = async (id: string, data: Partial<Integration>): Promise<Integration> => {
    const response = await designerApi.patch<Integration>(`/integrations/${id}`, data);
    return response.data;
};

// Delete an integration
export const deleteIntegration = async (id: string): Promise<void> => {
    await designerApi.delete(`/integrations/${id}`);
};

// Deploy integration container
export const deployIntegration = async (id: string, fullYaml: string): Promise<{ id: string; containerId: string }> => {
    const response = await designerApi.post<{ id: string; containerId: string }>(
        `/integrations/create/${id}`,
        fullYaml,
        { headers: { 'Content-Type': 'text/plain' } }
    );
    return response.data;
};

// Docker container operations
export const startContainer = async (containerId: string): Promise<void> => {
    await dockerApi.get('/start', { params: { containerId } });
};

export const stopContainer = async (containerId: string): Promise<void> => {
    await dockerApi.get('/stop', { params: { containerId } });
};

export const pauseContainer = async (containerId: string): Promise<void> => {
    await dockerApi.get('/pause', { params: { containerId } });
};

export const getContainerLogs = async (containerId: string): Promise<string[]> => {
    const response = await dockerApi.get<string[]>('/logs', { params: { containerId } });
    return response.data;
};

export const getContainerStatus = async (containerId: string): Promise<any[]> => {
    const response = await dockerApi.get<any[]>('/statuses', { params: { containerIds: containerId } });
    return response.data;
};

// Default YAML configurations
export const getDefaultYaml = (componentName: string): string => {
    const yamls: Record<string, string> = {
        imaps: `- route:
    id: route-imaps
    from:
      uri: "kamelet:mail-imap-source"
      parameters:
        connectionHost: ""
        connectionPort: "993"
        username: ""
        password: ""`,
        ftp: `- route:
    id: route-ftp
    from:
      uri: "kamelet:ftp-source"
      parameters:
        connectionHost: ""
        connectionPort: "21"
        username: ""
        password: ""
        directoryName: ""`,
        sftp: `- route:
    id: route-sftp
    from:
      uri: "kamelet:sftp-source"
      parameters:
        connectionHost: ""
        connectionPort: "22"
        username: ""
        password: ""
        directoryName: ""`,
        telegram: `- route:
    id: route-telegram
    from:
      uri: "kamelet:telegram-source"
      parameters:
        authorizationToken: ""`
    };
    return yamls[componentName] || '';
};

// Default Form.io JSON (Base64 encoded)
export const FORMIO_DEFAULT = btoa(JSON.stringify({ components: [] }));

// Export all functions as default object
export default {
    getAllComponents,
    getComponentSchema,
    getComponentList,
    createIntegration,
    patchIntegration,
    deleteIntegration,
    deployIntegration,
    startContainer,
    stopContainer,
    pauseContainer,
    getContainerLogs,
    getContainerStatus,
    getDefaultYaml,
    FORMIO_DEFAULT
};
